import { useState } from "react";
import { supabase } from "../lib/supabaseClient.ts";

interface EditFarmModalProps {
  open: boolean;
  onClose: () => void;
  farm: any; // Replace with your Farm type if you have one
  onFarmUpdated: () => void;
}

export default function EditFarmModal({
  open,
  onClose,
  farm,
  onFarmUpdated,
}: EditFarmModalProps) {
  const [name, setName] = useState(farm?.name || "");
  const [location, setLocation] = useState(farm?.location || "");
  const [area, setArea] = useState(farm?.area || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state if farm changes
  // You may want to add useEffect to reset fields when farm changes
  // Uncomment if modal stays mounted between uses
  // useEffect(() => {
  //   setName(farm?.name || "");
  //   setLocation(farm?.location || "");
  //   setArea(farm?.area || "");
  //   setError(null);
  // }, [farm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!name.trim()) {
      setError("Farm name is required.");
      setSaving(false);
      return;
    }

    // Optionally validate area as a number
    if (area && isNaN(Number(area))) {
      setError("Area must be a number.");
      setSaving(false);
      return;
    }

    const { error: updateError } = await supabase
      .from("farms")
      .update({ name, location, area })
      .eq("id", farm.id);

    if (updateError) {
      setError("Failed to update farm: " + updateError.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onFarmUpdated();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
        <button
          className="absolute text-xl text-gray-400 top-3 right-3 hover:text-gray-700"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="mb-4 text-lg font-semibold">Edit Farm</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">
              Name
              <input
                className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                disabled={saving}
              />
            </label>
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Location
              <input
                className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
                value={location}
                onChange={e => setLocation(e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Area (acres)
              <input
                className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
                value={area}
                onChange={e => setArea(e.target.value)}
                disabled={saving}
                type="number"
                min="0"
                step="any"
              />
            </label>
          </div>
          {error && <div className="text-red-600">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 font-semibold text-white bg-blue-600 rounded hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}